import { arrow, autoPlacement, computePosition, flip, offset } from '@floating-ui/dom'
import type { Coords, Placement } from '@floating-ui/core'

interface TooltipHandlerConfig {
  el: HTMLElement
  tooltipEl: HTMLElement
  arrowEl: HTMLElement
  onHide: (tooltipEl: HTMLElement) => void
  onShow: (tooltipEl: HTMLElement) => void
}

const boundHandlers = new Set<TooltipHandler>()
let sharedResizeListener: (() => void) | null = null

function ensureResizeListener(): void {
  if (sharedResizeListener) {
    return
  }

  sharedResizeListener = (): void => {
    boundHandlers.forEach((handler) => {
      handler.repositionIfVisible()
    })
  }

  window.addEventListener('resize', sharedResizeListener)
}

export default class TooltipHandler {
  private el: HTMLElement
  private tooltipEl: HTMLElement
  private arrowEl: HTMLElement
  private onHide: (tooltipEl: HTMLElement) => void
  private onShow: (tooltipEl: HTMLElement) => void

  constructor(config: TooltipHandlerConfig) {
    this.el = config.el
    this.tooltipEl = config.tooltipEl
    this.arrowEl = config.arrowEl
    this.onHide = config.onHide
    this.onShow = config.onShow
  }

  show(): void {
    this.onShow(this.tooltipEl)
    this.updatePosition()
      .then(() => true)
      .catch(() => true)
  }

  hide(e: MouseEvent | FocusEvent): void {
    const related = e.relatedTarget as Node | null

    if (e.type === 'mouseleave' && (this.el.contains(document.activeElement) || this.tooltipEl.contains(document.activeElement))) {
      return
    }

    if (!this.tooltipEl.contains(related) && !this.el.contains(related)) {
      this.onHide(this.tooltipEl)
    }
  }

  forceHide(): void {
    this.onHide(this.tooltipEl)
  }

  bind(signal: AbortSignal): void {
    const show = (): void => this.show()
    const hide = (e: MouseEvent | FocusEvent): void => this.hide(e)

    this.el.addEventListener('mouseenter', show, { signal })
    this.el.addEventListener('focusin', show, { signal })
    this.el.addEventListener('mouseleave', hide, { signal })
    this.el.addEventListener('focusout', hide, { signal })
    this.tooltipEl.addEventListener('mouseleave', hide, { signal })

    this.el.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && !this.tooltipEl.hasAttribute('hidden')) {
          this.forceHide()
        }
      },
      { signal }
    )

    boundHandlers.add(this)
    signal.addEventListener('abort', () => boundHandlers.delete(this))
    ensureResizeListener()
  }

  repositionIfVisible(): void {
    if (!this.tooltipEl.hasAttribute('hidden')) {
      this.updatePosition()
        .then(() => true)
        .catch(() => true)
    }
  }

  async updatePosition(): Promise<void> {
    if (!this.arrowEl) {
      return
    }

    const placementMiddleware = this.tooltipEl.hasAttribute('data-position') ? flip() : autoPlacement({ crossAxis: true })

    const { x, y, placement, middlewareData } = await computePosition(this.el, this.tooltipEl, {
      placement: (this.tooltipEl.getAttribute('data-position') ?? 'top') as Placement,
      middleware: [placementMiddleware, arrow({ element: this.arrowEl }), offset(6)],
    })

    this.tooltipEl.setAttribute('data-placement', placement)

    Object.assign(this.tooltipEl.style, {
      left: `${x}px`,
      top: `${y}px`,
    })

    const { x: arrowX, y: arrowY } = middlewareData.arrow as Coords

    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[placement.split('-')[0] as string] as string

    Object.assign(this.arrowEl.style, {
      left: arrowX != null ? `${arrowX}px` : '',
      top: arrowY != null ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide]: '-4px',
    })
  }
}
